/**
 * Revisão da extração de um documento antes de gravar. A IA lê o documento e propõe campos; o
 * operador confere, edita ou apaga cada um, e só então aplica no cadastro da entidade (pessoa ou
 * empresa). Dado de identidade não se grava no escuro — um CPF/CNPJ mal-lido funde/racha registros
 * (doc 04). Campos vazios não são aplicados; datas/valores que o backend não coage voltam em
 * "ignorados".
 */
import * as Dialog from '@radix-ui/react-dialog';
import { Check, Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { aplicarExtracao, type ExtracaoCad, ROTULO_CAMPO } from '@/lib/api';

export function ModalRevisaoExtracao({
  extracao,
  ownerTipo,
  ownerId,
  onFechar,
  onAplicado,
}: {
  extracao: ExtracaoCad | null;
  ownerTipo: string;
  ownerId: string;
  onFechar: () => void;
  onAplicado: () => void;
}) {
  const aberto = extracao !== null;
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [aplicando, setAplicando] = useState(false);

  useEffect(() => {
    setCampos(extracao ? { ...extracao.campos } : {});
  }, [extracao]);

  const chaves = Object.keys(campos);
  const algum = chaves.some((k) => campos[k]?.trim());

  async function aplicar() {
    if (!algum || aplicando) return;
    setAplicando(true);
    const r = await aplicarExtracao(ownerTipo, ownerId, campos);
    setAplicando(false);
    if (!r.ok) return void toast.error(r.erro);
    const { aplicados, ignorados } = r.dados;
    toast.success(
      `${aplicados.length} campo(s) gravado(s)` +
        (ignorados.length ? ` · ${ignorados.length} ignorado(s) (formato)` : ''),
    );
    onAplicado();
    onFechar();
  }

  return (
    <Dialog.Root open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-backdrop)] bg-[rgba(13,16,23,.6)] backdrop-blur-sm" />
        <Dialog.Content
          className={
            'fixed z-[var(--z-modal)] flex flex-col overflow-hidden bg-surface ' +
            'inset-0 md:inset-auto md:left-1/2 md:top-1/2 md:max-h-[85vh] md:w-[540px] ' +
            'md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg md:border md:border-border md:shadow-[var(--shadow-lg)]'
          }
        >
          <header className="flex items-center justify-between gap-3 border-b border-border p-4">
            <div className="min-w-0">
              <Dialog.Title className="tipo-subtitulo flex items-center gap-2 text-text">
                <Sparkles className="size-4 text-accent" />
                Revisar dados extraídos
              </Dialog.Title>
              <Dialog.Description className="tipo-corpo-pequeno text-text-tertiary">
                Lido pela IA ({extracao?.modelo}). Confira antes de gravar — apague o que estiver
                errado.
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label="Fechar"
              className="rounded-md p-2 text-text-secondary outline-none transition-colors hover:bg-surface-hover hover:text-text focus-visible:shadow-[var(--focus-ring)]"
            >
              <X className="size-4" />
            </Dialog.Close>
          </header>

          <div className="flex-1 overflow-y-auto p-4">
            {chaves.length === 0 ? (
              <p className="tipo-corpo-pequeno text-text-tertiary">
                A IA não encontrou dados legíveis neste documento.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {chaves.map((k) => (
                  <label key={k} className="flex flex-col gap-1">
                    <span className="tipo-rotulo">{ROTULO_CAMPO[k] ?? k}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        value={campos[k]}
                        onChange={(e) => setCampos((c) => ({ ...c, [k]: e.target.value }))}
                      />
                      <button
                        type="button"
                        aria-label={`Descartar ${ROTULO_CAMPO[k] ?? k}`}
                        title="Descartar este campo"
                        onClick={() => setCampos((c) => ({ ...c, [k]: '' }))}
                        className="rounded-md p-2 text-text-tertiary outline-none hover:text-danger"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <footer className="flex items-center justify-between gap-3 border-t border-border p-4">
            <span className="tipo-corpo-pequeno text-text-tertiary">
              Só campos preenchidos são gravados.
            </span>
            <div className="flex gap-2">
              <Button variant="secundario" onClick={onFechar}>
                Cancelar
              </Button>
              <Button variant="primario" onClick={aplicar} disabled={!algum || aplicando}>
                {aplicando ? <Spinner /> : <Check />}
                Aplicar ao cadastro
              </Button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
